// SearchableSelect — drop-in searchable replacement for shadcn <Select> when
// the option list gets long (category/subcategory pickers, unit codes, etc.).
//
// Contract mirrors shadcn Select but with a search input at the top of the
// popover:
//
//   <SearchableSelect
//     value={value}
//     onValueChange={setValue}
//     options={[{value:'ACC', primary:'ACC', secondary:'金具・小物・その他'}, ...]}
//     placeholder="選択…"
//     searchPlaceholder="コード・名称で検索"
//     disabled={false}
//     testid="sku-seg-subcategory"
//   />
//
// Matches when EITHER the primary (code) or secondary (name) contains the
// query — case-insensitive. Keyboard: type to filter · ↑↓ moves · Enter picks
// · Escape closes.
import { useEffect, useMemo, useRef, useState } from 'react';
import { CaretDown, MagnifyingGlass, Check } from '@phosphor-icons/react';
import {
  Popover, PopoverContent, PopoverTrigger,
} from './popover';
import { Input } from './input';

export function SearchableSelect({
  value,
  onValueChange,
  options = [],
  placeholder = '選択…',
  searchPlaceholder = 'コード・名称で検索',
  disabled = false,
  className = '',
  triggerClassName = '',
  testid,
  renderOption,
  emptyText = '一致する項目がありません',
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const filtered = useMemo(() => {
    const q = (query || '').trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => {
      const primary = String(o.primary || o.value || '').toLowerCase();
      const secondary = String(o.secondary || '').toLowerCase();
      return primary.includes(q) || secondary.includes(q);
    });
  }, [options, query]);

  // Reset local state when popover opens
  useEffect(() => {
    if (open) {
      setQuery('');
      setActiveIdx(0);
      // Focus search after popover mounts
      const t = setTimeout(() => inputRef.current?.focus(), 30);
      return () => clearTimeout(t);
    }
  }, [open]);

  // Keep active index in view
  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-idx="${activeIdx}"]`);
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [activeIdx, open]);

  const selected = useMemo(() => options.find((o) => o.value === value), [options, value]);

  const pick = (v) => {
    onValueChange?.(v);
    setOpen(false);
  };

  const onKey = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => Math.min(filtered.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter' && filtered[activeIdx]) {
      e.preventDefault();
      pick(filtered[activeIdx].value);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={(v) => !disabled && setOpen(v)}>
      <PopoverTrigger asChild disabled={disabled}>
        <button
          type="button"
          data-testid={testid}
          disabled={disabled}
          className={`flex h-10 w-full items-center justify-between rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm ring-offset-white placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-950 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${triggerClassName} ${className}`}
        >
          <span className={`truncate text-left ${!selected ? 'text-zinc-400' : ''}`}>
            {selected
              ? (renderOption
                ? renderOption(selected, true)
                : (
                  <span>
                    {selected.primary && <span className="font-mono font-bold">{selected.primary}</span>}
                    {selected.secondary && <span className="ml-2">{selected.secondary}</span>}
                  </span>
                ))
              : placeholder}
          </span>
          <CaretDown size={14} className="ml-2 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[--radix-popover-trigger-width]" align="start">
        <div className="p-2 border-b border-zinc-100 flex items-center gap-2">
          <MagnifyingGlass size={14} className="text-zinc-400 shrink-0" />
          <Input
            ref={inputRef}
            value={query}
            onChange={(e) => { setQuery(e.target.value); setActiveIdx(0); }}
            onKeyDown={onKey}
            placeholder={searchPlaceholder}
            className="h-8 border-0 focus-visible:ring-0 focus-visible:ring-offset-0 px-1 text-sm"
            data-testid={testid ? `${testid}-search` : undefined}
          />
        </div>
        <div ref={listRef} className="max-h-64 overflow-y-auto py-1" role="listbox">
          {filtered.length === 0 && (
            <div className="px-3 py-2 text-xs text-zinc-400 text-center">{emptyText}</div>
          )}
          {filtered.map((o, i) => {
            const isActive = i === activeIdx;
            const isSelected = o.value === value;
            return (
              <div
                key={o.value}
                data-idx={i}
                role="option"
                aria-selected={isSelected}
                onMouseEnter={() => setActiveIdx(i)}
                onClick={() => pick(o.value)}
                data-testid={testid ? `${testid}-opt-${o.value}` : undefined}
                className={`px-3 py-1.5 text-sm cursor-pointer flex items-center gap-2 ${isActive ? 'bg-indigo-50' : ''}`}
              >
                <span className="w-3 shrink-0 text-emerald-600">
                  {isSelected ? <Check size={12} weight="bold" /> : null}
                </span>
                <span className="min-w-0 flex-1">
                  {renderOption
                    ? renderOption(o, false)
                    : (
                      <>
                        {o.primary && <span className="font-mono font-bold text-zinc-800">{o.primary}</span>}
                        {o.secondary && <span className="ml-2 text-zinc-600">{o.secondary}</span>}
                      </>
                    )}
                </span>
              </div>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
